import React, { useContext, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faChevronDown, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { toZonedTime } from 'date-fns-tz';
import './styles.css';
import './DateSelector.css';
import { RootStoreContext } from 'src/stores/RootStore';
import EarlyCheckinPromoLine from './EarlyCheckinPromoLine';

interface DateSelectorProps {
  onDateSelect: (date: Date) => void;
  checkInFutureDays: number;
  showDiscountPromo?: boolean;
  disabled?: boolean;
  displayMode?: 'dropdown' | 'calendar' | 'strip';
  bookedDateKeys?: Set<string>;
}

const TALLINN_TIME_ZONE = 'Europe/Tallinn';
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const dateKey = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const startOfMonth = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), 1);

const monthKey = (date: Date): number => date.getFullYear() * 12 + date.getMonth();

const DateSelector: React.FC<DateSelectorProps> = ({
  onDateSelect,
  checkInFutureDays,
  showDiscountPromo = true,
  disabled = false,
  displayMode = 'dropdown',
  bookedDateKeys = new Set()
}) => {
  const { studioConfigStore } = useContext(RootStoreContext);
  const [isOpen, setIsOpen] = useState(false);
  const storeSelectedDateValue = studioConfigStore.selectedDate?.getTime();
  const [selectedDate, setSelectedDate] = useState(() =>
    new Date(studioConfigStore.selectedDate)
  );
  const [visibleMonth, setVisibleMonth] = useState(() =>
    startOfMonth(studioConfigStore.selectedDate || new Date())
  );

  useEffect(() => {
    if (!studioConfigStore.studioConfigs) {
      studioConfigStore.fetchStudioConfigs();
    }
  }, [studioConfigStore]);

  useEffect(() => {
    setSelectedDate(new Date(studioConfigStore.selectedDate));
  }, [storeSelectedDateValue, studioConfigStore.selectedDate]);

  const serverDateTime = studioConfigStore.studioConfigs?.serverDateTime;
  const selectedDateValue = selectedDate.getTime();

  const calendarData = useMemo(() => {
    if (!serverDateTime || !Number.isInteger(checkInFutureDays) || checkInFutureDays < 2) {
      return null;
    }

    // Keep the configured server clock as the source of truth, expressed in Tallinn time.
    const today = toZonedTime(new Date(serverDateTime), TALLINN_TIME_ZONE);
    const allowedDates: Date[] = [];
    const allowedDatesByKey = new Map<string, Date>();

    for (let offset = 0; offset < checkInFutureDays; offset += 1) {
      const date = new Date(today);
      date.setDate(today.getDate() + offset);
      allowedDates.push(date);
      allowedDatesByKey.set(dateKey(date), date);
    }

    const lastDate = allowedDates[allowedDates.length - 1];
    const selectedIsAllowed = allowedDatesByKey.has(dateKey(selectedDate));

    return {
      today,
      selectedDate,
      selectedIsAllowed,
      allowedDatesByKey,
      firstMonth: startOfMonth(today),
      lastMonth: startOfMonth(lastDate)
    };
  }, [checkInFutureDays, selectedDate, selectedDateValue, serverDateTime]);

  useEffect(() => {
    if (!calendarData) return;
    setVisibleMonth(startOfMonth(
      calendarData.selectedIsAllowed ? calendarData.selectedDate : calendarData.today
    ));
  }, [calendarData]);

  if (!calendarData) {
    return null;
  }

  const {
    today,
    selectedDate: displayedSelectedDate,
    allowedDatesByKey,
    firstMonth,
    lastMonth
  } = calendarData;
  const firstDayOffset = (visibleMonth.getDay() + 6) % 7;
  const gridStart = new Date(
    visibleMonth.getFullYear(),
    visibleMonth.getMonth(),
    1 - firstDayOffset
  );
  const calendarDays = Array.from({ length: 42 }, (_, index) => {
    const date = new Date(gridStart);
    date.setDate(gridStart.getDate() + index);
    return date;
  });
  const canGoPrevious = monthKey(visibleMonth) > monthKey(firstMonth);
  const canGoNext = monthKey(visibleMonth) < monthKey(lastMonth);

  const changeMonth = (offset: number) => {
    if (disabled) return;
    setVisibleMonth(current => new Date(current.getFullYear(), current.getMonth() + offset, 1));
  };

  const handleDateSelect = (date: Date) => {
    if (disabled) return;
    const allowedDate = allowedDatesByKey.get(dateKey(date));
    if (allowedDate) {
      const nextDate = new Date(allowedDate);
      setSelectedDate(nextDate);
      onDateSelect(nextDate);
      setIsOpen(false);
    }
  };

  const formatDate = (date: Date): string => {
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    const label = date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
    if (dateKey(date) === dateKey(today)) return `Today (${label})`;
    if (dateKey(date) === dateKey(tomorrow)) return `Tomorrow (${label})`;
    return label;
  };

  return (
    <div className="date-selector-container">
      {showDiscountPromo && <EarlyCheckinPromoLine />}
      <div className="date-selector-title">
        <FontAwesomeIcon icon={faCalendarAlt} className="date-icon" />
        <span>Select Date</span>
      </div>

      {displayMode === 'strip' ? (
        <div
          className={`booking-date-strip ${disabled ? 'disabled' : ''}`}
          aria-label="Available booking dates"
          aria-disabled={disabled}
        >
          {Array.from(allowedDatesByKey.values()).map(date => {
            const key = dateKey(date);
            const isSelected = key === dateKey(displayedSelectedDate);
            const hasBooking = bookedDateKeys.has(key);
            const accessibleDate = date.toLocaleDateString('en-US', {
              weekday: 'long',
              month: 'long',
              day: 'numeric',
              year: 'numeric'
            });

            return (
              <button
                type="button"
                key={key}
                className={`booking-date-card ${isSelected ? 'selected' : ''} ${hasBooking ? 'has-booking' : ''}`}
                aria-label={`${accessibleDate}${isSelected ? ', selected' : ''}${hasBooking ? ', booked' : ''}`}
                aria-pressed={isSelected}
                disabled={disabled}
                onClick={() => handleDateSelect(date)}
                style={date === Array.from(allowedDatesByKey.values())[0]
                  ? { gridColumnStart: ((date.getDay() + 6) % 7) + 1 }
                  : undefined}
              >
                <span className="booking-date-weekday">
                  {date.toLocaleDateString('en-US', { weekday: 'short' })}
                </span>
                <span className="booking-date-day">
                  {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </span>
                <span className="booking-date-indicator" aria-hidden="true" />
              </button>
            );
          })}
        </div>
      ) : displayMode === 'calendar' ? (
        <div className={`booking-calendar ${disabled ? 'disabled' : ''}`} aria-disabled={disabled}>
          <div className="booking-calendar-header">
            <button
              type="button"
              className="booking-calendar-nav"
              aria-label="Previous month"
              disabled={disabled || !canGoPrevious}
              onClick={() => changeMonth(-1)}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <div className="booking-calendar-month" aria-live="polite">
              {visibleMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </div>
            <button
              type="button"
              className="booking-calendar-nav"
              aria-label="Next month"
              disabled={disabled || !canGoNext}
              onClick={() => changeMonth(1)}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>

          <div className="booking-calendar-weekdays" aria-hidden="true">
            {WEEKDAYS.map(day => <span key={day}>{day}</span>)}
          </div>

          <div className="booking-calendar-grid" role="group" aria-label="Available booking dates">
            {calendarDays.map(date => {
              const key = dateKey(date);
              const isAllowed = allowedDatesByKey.has(key);
              const isSelected = key === dateKey(displayedSelectedDate);
              const isToday = key === dateKey(today);
              const isOutsideMonth = date.getMonth() !== visibleMonth.getMonth();
              const accessibleDate = date.toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
                year: 'numeric'
              });

              return (
                <button
                  type="button"
                  key={key}
                  className={[
                    'booking-calendar-day',
                    isSelected ? 'selected' : '',
                    isToday ? 'today' : '',
                    isOutsideMonth ? 'outside-month' : ''
                  ].filter(Boolean).join(' ')}
                  aria-label={`${accessibleDate}${isToday ? ', today' : ''}${isSelected ? ', selected' : ''}`}
                  aria-pressed={isSelected}
                  disabled={disabled || !isAllowed}
                  onClick={() => handleDateSelect(date)}
                >
                  <span>{date.getDate()}</span>
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="date-dropdown">
          <div
            className={`date-dropdown-toggle ${disabled ? 'disabled' : ''}`}
            aria-disabled={disabled}
            onClick={() => {
              if (!disabled) setIsOpen(current => !current);
            }}
          >
            <span className="date-dropdown-selected">{formatDate(displayedSelectedDate)}</span>
            <FontAwesomeIcon
              icon={faChevronDown}
              className={`date-dropdown-arrow ${isOpen ? 'open' : ''}`}
            />
          </div>
          {isOpen && (
            <div className="date-dropdown-menu">
              {Array.from(allowedDatesByKey.values()).map(date => (
                <div
                  key={dateKey(date)}
                  className={`date-dropdown-item ${dateKey(date) === dateKey(displayedSelectedDate) ? 'active' : ''}`}
                  onClick={() => handleDateSelect(date)}
                >
                  {formatDate(date)}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default observer(DateSelector);
